import { Card } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useTransition } from "react";
import CommentsFeature from "components/comment/comments";
import { CommentTypes } from "types/Comment";
import UploadFile from "features/upload-file/upload-file";
import Files from "features/files/files";
import Products from "components/customer/products/products";
import { BlockStack, Text, ButtonGroup } from "@shopify/polaris";
import { ProductForCustomer } from "types/Product";
import ProjectActivityCompose from "components/project/project-activity-compose/project-activity-compose";
import ProjectFocusPanel from "components/project/project-focus-panel/project-focus-panel";
import ProjectHistoryPanel from "components/project/project-history-panel/project-history-panel";
import ProjectEmailPanel from "components/project/project-email-panel/project-email-panel";
import { useProjectHistoryFeed } from "components/project/project-history-feed/use-project-history-feed";
import {
  ProjectTabContentSkeleton,
  ProjectTabSkeletonId,
} from "components/project/project-tab-skeletons/project-tab-skeletons";

const DETAIL_TABS = [
  { id: "activity", labelKey: "project_tab_activity", defaultLabel: "Activity" },
  { id: "notes", labelKey: "project_tab_notes", defaultLabel: "Notes" },
  { id: "email", labelKey: "project_tab_email", defaultLabel: "Email" },
  { id: "files", labelKey: "files", defaultLabel: "Files" },
  { id: "products", labelKey: "products", defaultLabel: "Products" },
] as const;

type TabId = (typeof DETAIL_TABS)[number]["id"];

/** Brief skeleton window so tab switches feel intentional, not abrupt. */
const TAB_SKELETON_MS = 220;

type ProjectDetails = {
  id: string;
  name: string;
  user?: { id: string } | null;
  contactEmail?: string | null;
  contactName?: string | null;
  files?: unknown[];
  products?: ProductForCustomer[];
};

type Props = {
  companyId: string;
  projectId: string;
  projectDetails: ProjectDetails;
  activeTab: TabId;
  onTabChange: (tab: TabId) => void;
  onFileChange?: () => void;
};

export default function ProjectDetailMainColumn({
  companyId,
  projectId,
  projectDetails,
  activeTab,
  onTabChange,
  onFileChange = () => {},
}: Props) {
  const { t } = useTranslation();
  const { focus, history, counts, loading, reload } = useProjectHistoryFeed(
    companyId,
    projectId
  );
  const [isPending, startTransition] = useTransition();
  const [displayTab, setDisplayTab] = useState<TabId>(activeTab);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (activeTab === displayTab) return;

    setShowSkeleton(true);
    const timer = window.setTimeout(() => {
      startTransition(() => {
        setDisplayTab(activeTab);
        setShowSkeleton(false);
      });
    }, TAB_SKELETON_MS);

    return () => window.clearTimeout(timer);
  }, [activeTab, displayTab]);

  const handleTabClick = (tabId: TabId) => {
    if (tabId === activeTab) return;
    onTabChange(tabId);
  };

  const tabBusy = showSkeleton || isPending;
  const skeletonTab = (showSkeleton ? activeTab : displayTab) as ProjectTabSkeletonId;

  return (
    <div className="project-detail-tabs flex min-w-0 flex-col gap-4">
      <Card roundedAbove="sm" padding="0">
        <div className="flex gap-1 overflow-x-auto border-b border-[var(--p-color-border)] px-3 pt-2">
          {DETAIL_TABS.map((tab) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabClick(tab.id)}
                className={`shrink-0 border-b-2 px-3 py-2 text-sm font-semibold transition-colors duration-150 ${
                  selected
                    ? "border-[var(--p-color-border-focus)] text-[var(--p-color-text)]"
                    : "border-transparent text-[var(--p-color-text-secondary)] hover:text-[var(--p-color-text)]"
                }`}
              >
                {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
              </button>
            );
          })}
        </div>

        <div className="p-4 min-h-[12rem]">
          {tabBusy ? (
            <div key={`sk-${skeletonTab}`} className="project-tab-fade-in">
              <ProjectTabContentSkeleton tab={skeletonTab} />
            </div>
          ) : (
            <div key={displayTab} className="project-tab-fade-in">
              {displayTab === "activity" && (
                <ProjectActivityCompose
                  companyId={companyId}
                  projectId={projectId}
                  cardName={projectDetails.name}
                  defaultAssigneeId={projectDetails.user?.id || null}
                  onSaved={reload}
                />
              )}

              {displayTab === "notes" && (
                <CommentsFeature
                  id={projectId}
                  CommentTypes={CommentTypes.Projects}
                />
              )}

              {displayTab === "email" && (
                <ProjectEmailPanel
                  companyId={companyId}
                  projectId={projectId}
                  contactEmail={projectDetails.contactEmail}
                  contactName={projectDetails.contactName}
                  cardName={projectDetails.name}
                />
              )}

              {displayTab === "files" && (
                <BlockStack gap="300">
                  <div className="flex items-center">
                    <Text as="h3" variant="headingSm">
                      {t("files")}
                    </Text>
                    <div className="flex-grow" />
                    <ButtonGroup>
                      <UploadFile
                        onChange={onFileChange}
                        subject="project"
                        subjectId={projectId}
                      />
                    </ButtonGroup>
                  </div>
                  <Files files={projectDetails.files ?? []} onChanged={onFileChange} />
                </BlockStack>
              )}

              {displayTab === "products" && (
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    {t("products")}
                  </Text>
                  <Products
                    products={projectDetails.products ?? []}
                    isProjectTbl={true}
                  />
                </BlockStack>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card roundedAbove="sm" padding="0">
        <ProjectFocusPanel
          companyId={companyId}
          projectId={projectId}
          items={focus}
          loading={loading}
          cardName={projectDetails.name}
          onChanged={reload}
        />
      </Card>

      <Card roundedAbove="sm" padding="0">
        <ProjectHistoryPanel
          companyId={companyId}
          projectId={projectId}
          items={history}
          counts={counts}
          loading={loading}
          onChanged={reload}
        />
      </Card>
    </div>
  );
}

export { DETAIL_TABS };
export type { TabId };
